// STEP 3-WMS-B PHASE 2-C-FRONTEND · Seller direct-shipping combined shipment UX.
// REUSES existing backend APIs · NO backend changes.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Package, X, ExternalLink } from 'lucide-react';
import api from '@/lib/api';
import { getTrackingAvailable, getTrackingUrl } from '@/lib/trackingUrl';

const _mapErr = (e) => {
  const s = e?.response?.status; const d = e?.response?.data?.detail;
  const str = typeof d === 'string' ? d : (d?.detail || '');
  if (s === 403 && str.includes('다른 판매자')) return '다른 판매자 주문은 접근할 수 없습니다.';
  if (s === 403 && str.includes('3PL')) return '3PL 배정 주문은 물류센터에서 합포 처리합니다.';
  if (s === 400 && str.includes('3PL')) return '3PL 배정 주문은 물류센터에서 합포 처리합니다.';
  if (s === 400 && str.includes('수령인')) return '수령인/주소가 일치하지 않는 주문이 포함되어 있습니다.';
  if (s === 409 && str.includes('이미')) return '이미 다른 합포 그룹에 포함된 주문이 있습니다.';
  if (s === 400 && str.includes('송장')) return '대표 송장이 등록되지 않았습니다.';
  if (s === 409) return str || '재시도 필요 · 일부 처리 실패';
  if (s === 403) return '이 작업을 처리할 권한이 없습니다.';
  return str || '요청 실패';
};

const _uuid = () => (crypto?.randomUUID?.() || `cr-${Date.now()}-${Math.random().toString(36).slice(2,10)}`);

export default function SellerCombinedShipmentModal({ open, onClose, onChanged }) {
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null); // {orders:[...]}
  const [activeCid, setActiveCid] = useState(null);         // current group detail
  const [cidDoc, setCidDoc] = useState(null);
  const [inv, setInv] = useState({ courier: 'CJ대한통운', tracking_number: '' });
  const [shipConfirm, setShipConfirm] = useState(false);
  const [deliverConfirm, setDeliverConfirm] = useState(false);

  const loadCandidates = async () => {
    try { const r = await api.get('/seller/combined-shipments/candidates');
      setGroups(r.data.items || []);
    } catch (e) { toast.error(_mapErr(e)); }
  };
  const loadCid = async (cid) => {
    try { const r = await api.get(`/seller/combined-shipments/${cid}`);
      setCidDoc(r.data); setActiveCid(cid);
    } catch (e) { toast.error(_mapErr(e)); }
  };
  useEffect(() => { if (open) { loadCandidates(); setActiveCid(null); setCidDoc(null); setSelectedGroup(null); } }, [open]);
  if (!open) return null;

  const doConfirm = async (g) => {
    setBusy(true);
    try {
      const r = await api.post('/seller/combined-shipments', {
        seller_order_ids: g.orders.map((o) => o.seller_order_id),
        client_ref: _uuid(),
      });
      toast.success('📦 합포 확정 완료');
      setSelectedGroup(null);
      await loadCandidates();
      await loadCid(r.data.combined_shipment_id);
      onChanged?.();
    } catch (e) { toast.error(_mapErr(e)); }
    finally { setBusy(false); }
  };

  const doTracking = async () => {
    if (!inv.courier || !inv.tracking_number) return toast.error('택배사와 송장번호가 필요합니다.');
    setBusy(true);
    try {
      await api.post(`/seller/combined-shipments/${activeCid}/tracking`, {
        courier: inv.courier, tracking_number: inv.tracking_number.trim(),
        shipping_mode: 'INTEGRATED',
      });
      toast.success('대표 송장 등록 완료');
      await loadCid(activeCid);
      onChanged?.();
    } catch (e) { toast.error(_mapErr(e)); }
    finally { setBusy(false); }
  };

  const doShip = async () => {
    setBusy(true);
    try {
      await api.post(`/seller/combined-shipments/${activeCid}/ship`);
      toast.success('📦 합포 출고 완료');
      setShipConfirm(false);
      await loadCid(activeCid);
      onChanged?.();
    } catch (e) { toast.error(_mapErr(e)); }
    finally { setBusy(false); }
  };

  const doDeliver = async () => {
    setBusy(true);
    try {
      await api.post(`/seller/combined-shipments/${activeCid}/delivered`);
      toast.success('✅ 합포 배송완료 처리 완료');
      setDeliverConfirm(false);
      await loadCid(activeCid);
      onChanged?.();
    } catch (e) { toast.error(_mapErr(e)); }
    finally { setBusy(false); }
  };

  const sh = cidDoc?.shipment || {};
  const hasTracking = Boolean(sh.tracking_number);
  const shipped = cidDoc?.status === 'SHIPPED';
  const delivered = cidDoc?.status === 'DELIVERED';
  const trackUrl = hasTracking && getTrackingAvailable(sh.courier, sh.tracking_number)
    ? getTrackingUrl(sh.courier, sh.tracking_number) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-lg max-w-lg w-full p-4 space-y-3 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="seller-combined-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-bold flex items-center gap-1"><Package size={16}/> 판매자 직접배송 합포</h3>
          <button onClick={onClose} disabled={busy}><X size={16}/></button>
        </div>
        {!activeCid && !selectedGroup && (
          <>
            <p className="text-xs text-neutral-500">직접배송 주문 중 수령인이 같은 그룹만 표시됩니다. 3PL 배정 주문은 물류센터에서 합포 처리됩니다.</p>
            {groups.length === 0 ? (
              <p className="text-sm text-neutral-500 py-4 text-center" data-testid="seller-combined-empty">현재 합포 가능한 후보가 없습니다.</p>
            ) : groups.map((g, i) => (
              <button key={i} onClick={() => setSelectedGroup(g)}
                       className="block w-full text-left rounded border p-2 hover:bg-neutral-50 text-xs"
                       data-testid={`seller-combined-candidate-${i}`}>
                <p className="font-bold">📦 {g.orders[0].recipient_name} · {g.orders.length}건</p>
                <p className="text-neutral-500">{g.orders[0].phone}</p>
                {g.orders.map((o) => <p key={o.seller_order_id} className="font-mono">· {o.order_number || o.seller_order_id}</p>)}
              </button>
            ))}
          </>
        )}
        {selectedGroup && !activeCid && (
          <>
            <div className="text-xs bg-neutral-50 p-2 rounded">
              <p><b>수령인:</b> {selectedGroup.orders[0].recipient_name}</p>
              <p><b>전화:</b> {selectedGroup.orders[0].phone}</p>
            </div>
            <div className="text-xs space-y-1">
              <p className="font-bold">포함 주문 {selectedGroup.orders.length}건</p>
              {selectedGroup.orders.map((o) => (
                <div key={o.seller_order_id} className="border rounded px-2 py-1">
                  <p className="font-mono">{o.order_number || o.seller_order_id}</p>
                  <p className="text-neutral-500">상품 {o.items_count}종</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setSelectedGroup(null)} disabled={busy} className="px-3 py-1.5 rounded border text-sm">취소</button>
              <button onClick={() => doConfirm(selectedGroup)} disabled={busy}
                       className="px-3 py-1.5 rounded bg-[#FF3B30] text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
                       data-testid="seller-combined-confirm-btn">
                {busy && <Loader2 size={12} className="animate-spin"/>} 📦 합포 확정
              </button>
            </div>
          </>
        )}
        {activeCid && cidDoc && (
          <div className="space-y-2 text-xs" data-testid="seller-combined-group-panel">
            <div className="rounded bg-amber-50 border border-amber-200 p-2">
              <p className="font-bold text-amber-800">📦 합포배송 · <span className="font-mono">{activeCid}</span></p>
              <p>상태: <b>{cidDoc.status}</b> · 포함 주문 {(cidDoc.members || []).length}건</p>
              {(cidDoc.members || []).map((m) => (
                <div key={m.seller_order_id} className="flex items-center justify-between mt-1">
                  <p className="font-mono">· {m.seller_order_id}</p>
                  {!hasTracking && !shipped && !delivered && cidDoc.status !== 'CANCELLED' && (
                    <button onClick={async () => {
                      setBusy(true);
                      try {
                        await api.delete(`/seller/combined-shipments/${activeCid}/members/${m.seller_order_id}`);
                        toast.success('합포에서 제외되었습니다.');
                        await loadCid(activeCid); onChanged?.();
                      } catch (e) { toast.error(_mapErr(e)); }
                      finally { setBusy(false); }
                    }} disabled={busy}
                       className="text-[10px] px-1.5 py-0.5 rounded border border-red-300 text-red-700 disabled:opacity-50"
                       data-testid={`seller-combined-remove-${m.seller_order_id}`}>빼기</button>
                  )}
                </div>
              ))}
              {!hasTracking && !shipped && !delivered && cidDoc.status !== 'CANCELLED' && (
                <button onClick={async () => {
                  if (!window.confirm('합포를 전체 해제합니다. 계속하시겠습니까?')) return;
                  setBusy(true);
                  try {
                    await api.post(`/seller/combined-shipments/${activeCid}/ungroup`);
                    toast.success('합포가 해제되었습니다.');
                    await loadCid(activeCid); onChanged?.();
                  } catch (e) { toast.error(_mapErr(e)); }
                  finally { setBusy(false); }
                }} disabled={busy}
                   className="mt-2 w-full px-2 py-1 rounded border border-red-300 text-red-700 text-[11px] font-bold disabled:opacity-50"
                   data-testid="seller-combined-ungroup-btn">🔓 합포 전체 해제</button>
              )}
              {(hasTracking || shipped || delivered) && (
                <p className="text-[10px] text-neutral-500 mt-1">송장 등록 후에는 합포를 변경할 수 없습니다.</p>
              )}
            </div>
            {hasTracking ? (
              <div className="rounded border p-2">
                <p><b>택배사:</b> {sh.courier}</p>
                <p><b>송장번호:</b> <span className="font-mono">{sh.tracking_number}</span></p>
                {trackUrl && <a href={trackUrl} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 mt-1 font-bold text-[#FF3B30] underline underline-offset-2" data-testid="seller-combined-track">
                  <ExternalLink size={11}/> 배송조회 ↗
                </a>}
              </div>
            ) : (
              <div className="rounded border p-2 space-y-1">
                <p className="font-bold">대표 송장 등록</p>
                <div className="flex gap-1">
                  <input value={inv.courier} onChange={(e) => setInv({...inv, courier: e.target.value})} placeholder="택배사" className="flex-1 border rounded px-2 py-1" data-testid="seller-combined-tracking-courier"/>
                  <input value={inv.tracking_number} onChange={(e) => setInv({...inv, tracking_number: e.target.value})} placeholder="송장번호" className="flex-1 border rounded px-2 py-1 font-mono" data-testid="seller-combined-tracking-number"/>
                </div>
                <button onClick={doTracking} disabled={busy || !inv.tracking_number} className="px-3 py-1 rounded bg-[#FF3B30] text-white inline-flex items-center gap-1 disabled:opacity-50" data-testid="seller-combined-tracking-submit">
                  {busy && <Loader2 size={10} className="animate-spin"/>} 대표 송장 등록
                </button>
              </div>
            )}
            {!shipped && hasTracking && (
              <button onClick={() => setShipConfirm(true)} disabled={busy}
                       className="w-full px-3 py-1.5 rounded bg-[#FF3B30] text-white font-bold inline-flex items-center justify-center gap-1 disabled:opacity-50"
                       data-testid="seller-combined-ship-btn">📦 합포 출고</button>
            )}
            {shipped && (
              <button onClick={() => setDeliverConfirm(true)} disabled={busy}
                       className="w-full px-3 py-1.5 rounded bg-emerald-600 text-white font-bold inline-flex items-center justify-center gap-1 disabled:opacity-50"
                       data-testid="seller-combined-deliver-btn">✅ 배송완료 처리</button>
            )}
            {delivered && <p className="text-emerald-800 font-bold text-center" data-testid="seller-combined-delivered-label">✅ 배송완료</p>}
            {deliverConfirm && (
              <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setDeliverConfirm(false)}>
                <div className="bg-white rounded-lg max-w-sm w-full p-4 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="seller-combined-deliver-confirm">
                  <h3 className="font-bold">✅ 합포 배송완료 처리</h3>
                  <p className="text-sm">합포 배송의 모든 주문을 배송완료 처리합니다.</p>
                  <ul className="text-xs font-mono bg-neutral-50 rounded p-2">{(cidDoc.members || []).map((m) => <li key={m.seller_order_id}>· {m.seller_order_id}</li>)}</ul>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setDeliverConfirm(false)} disabled={busy} className="px-3 py-1.5 rounded border text-sm">취소</button>
                    <button onClick={doDeliver} disabled={busy} className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="seller-combined-deliver-confirm-btn">
                      {busy && <Loader2 size={12} className="animate-spin"/>} 배송완료 처리
                    </button>
                  </div>
                </div>
              </div>
            )}
            {shipConfirm && (
              <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setShipConfirm(false)}>
                <div className="bg-white rounded-lg max-w-sm w-full p-4 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="seller-combined-ship-confirm">
                  <h3 className="font-bold">📦 합포 출고</h3>
                  <p className="text-sm">{(cidDoc.members || []).length}개 주문을 하나의 배송으로 출고합니다.</p>
                  <ul className="text-xs font-mono bg-neutral-50 rounded p-2">{(cidDoc.members || []).map((m) => <li key={m.seller_order_id}>· {m.seller_order_id}</li>)}</ul>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShipConfirm(false)} disabled={busy} className="px-3 py-1.5 rounded border text-sm">취소</button>
                    <button onClick={doShip} disabled={busy} className="px-3 py-1.5 rounded bg-[#FF3B30] text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="seller-combined-ship-confirm-btn">
                      {busy && <Loader2 size={12} className="animate-spin"/>} 합포 출고
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
